const MESSAGE_ID_SELECTOR = '[data-message-id]';

interface ScrollMessageIntoViewByIdArgs {
  container: HTMLElement | null;
  messageId: string;
  behavior?: ScrollBehavior;
}

type ScrollMessageIntoViewByIdResult =
  | {
      didScroll: false;
      hasContainer: boolean;
      hasElement: boolean;
      renderedMessageCount: number;
    }
  | {
      didScroll: true;
      hasContainer: true;
      hasElement: true;
      renderedMessageCount: number;
      targetScrollTop: number;
    };

export const findMessageElementById = (
  container: HTMLElement | null,
  messageId: string
): HTMLElement | undefined => {
  if (!container) {
    return undefined;
  }

  return Array.from(container.querySelectorAll<HTMLElement>(MESSAGE_ID_SELECTOR)).find(
    (element) => element.dataset.messageId === messageId
  );
};

export const scrollMessageIntoViewById = ({
  container,
  messageId,
  behavior = 'smooth',
}: ScrollMessageIntoViewByIdArgs): ScrollMessageIntoViewByIdResult => {
  const renderedMessageCount = container?.querySelectorAll(MESSAGE_ID_SELECTOR).length ?? 0;
  const messageElement = findMessageElementById(container, messageId);

  if (!container || !messageElement) {
    return {
      didScroll: false,
      hasContainer: Boolean(container),
      hasElement: Boolean(messageElement),
      renderedMessageCount,
    };
  }

  const containerRect = container.getBoundingClientRect();
  const messageRect = messageElement.getBoundingClientRect();
  const rawTop = container.scrollTop + (messageRect.top - containerRect.top);
  const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
  const targetScrollTop = Math.min(Math.max(rawTop, 0), maxScrollTop);

  container.scrollTo({
    top: targetScrollTop,
    behavior,
  });

  return {
    didScroll: true,
    hasContainer: true,
    hasElement: true,
    renderedMessageCount,
    targetScrollTop,
  };
};

export type { ScrollMessageIntoViewByIdArgs, ScrollMessageIntoViewByIdResult };